import { useCallback, useMemo } from 'react';

import { Button } from '../../../buttons';
import { ChevronLeftIcon, ChevronRightIcon } from '../../../foundations';
import { SEPARATOR } from '../pagination-config';
import { usePaginationContext } from '../pagination-context';
import { PaginationNavigationItem } from '../pagination-navigation-item';

import { getNavItems } from './pagination-navigation-helpers';

import type { ReactElement } from 'react';

const PaginationNavigation = (): null | ReactElement => {
  const paginationContext = usePaginationContext();

  if (!paginationContext) {
    throw new Error('PaginationNavigation must be used within a PaginationContext.Provider');
  }

  const { count, itemsPerPage, onPageChange, page } = paginationContext;

  const totalPages = Math.ceil(count / itemsPerPage);

  const onNext = useCallback((): void => {
    onPageChange?.(page + 1);
  }, [onPageChange, page]);

  const onPrev = useCallback((): void => {
    onPageChange?.(page - 1);
  }, [onPageChange, page]);

  const navItemsWithKeys = useMemo(() => {
    const navItems = getNavItems(page, totalPages);
    let separatorCount = 0;
    return navItems.map((item) => {
      if (item === SEPARATOR) {
        separatorCount++;
        return { item, key: `rbui-pagination-separator-${separatorCount}` };
      }
      return { item, key: `rbui-pagination-item-${item}` };
    });
  }, [page, totalPages]);

  if (count <= itemsPerPage) {
    return null;
  }

  return (
    <nav aria-label="Pagination Navigation" className="rbui-pagination__navigation" role="navigation">
      <ul>
        <li>
          <Button aria-label="Go to previous page" disabled={page === 0} onClick={onPrev} type="tertiary">
            <ChevronLeftIcon />
          </Button>
        </li>
        {navItemsWithKeys.map(({ item, key }) => (
          <li key={key}>
            <PaginationNavigationItem value={item} />
          </li>
        ))}
        <li>
          <Button aria-label="Go to next page" disabled={page >= totalPages - 1} onClick={onNext} type="tertiary">
            <ChevronRightIcon />
          </Button>
        </li>
      </ul>
    </nav>
  );
};

PaginationNavigation.displayName = 'PaginationNavigation';

export { PaginationNavigation };
